import { DatePipe } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import {
  ConfirmationService,
  ConfirmEventType,
  MenuItem,
  MessageService,
} from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { AppSettings } from '../Constant/AppSettings';
import { DiscountService } from '../services/discount/discount.service';
import { PrivilegeService } from '../services/privilege/privilege.service';
import { UtilsService } from '../services/utils/utils.service';

@Component({
  selector: 'app-manage-discount',
  templateUrl: './manage-discount.component.html',
  styleUrls: ['./manage-discount.component.css'],
})
export class ManageDiscountComponent implements OnInit {
  @Input() id: any;

  items: MenuItem[] = [{ label: 'Manage Discount' }];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  isLoading = false;
  disabled = false;
  public innerWidth: any;

  // Form fields
  name = '';
  description = '';
  discountType = 'PERCENT';
  discountValue: number | null = null;
  discountValidity = 'LIMITED';
  discountValidityValue: number | null = 0;
  discountDuration = 'LIMITED';
  discountDurationValue: number | null = 0;
  discountFor = 'SUBSCRIPTION_WITHOUT_SMS';
  discountSubscriptionType = 'ALL';
  discountSubscriptionDuration = 'ANNUAL';
  discountIndustries: string[] = [];
  status = 'ACTIVE';
  discountActivation = 'immediate';
  discountActivationDate!: Date;
  minDate!: Date;

  discounts: any[] = [];
  displayAddModal = false;
  displayEditModal = false;
  activeIndustries: string[] = [];

  discountTypeOptions = [
    { label: 'Percent (% off)', value: 'PERCENT' },
    { label: 'Value (fixed / SMS credits)', value: 'VALUE' },
  ];

  discountValidityOptions = [
    { label: 'Limited', value: 'LIMITED' },
    { label: 'Unlimited', value: 'UNLIMITED' },
  ];

  discountDurationOptions = [
    { label: 'Limited (days)', value: 'LIMITED' },
    { label: 'Unlimited', value: 'UNLIMITED' },
  ];

  discountForOptions = [
    { label: 'Subscription', value: 'SUBSCRIPTION_WITHOUT_SMS' },
    { label: 'SMS Credits', value: 'SMS' },
    { label: 'Subscription + SMS Credits', value: 'SUBSCRIPTION_WITH_SMS' },
  ];

  subscriptionTypeOptions = [
    { label: 'All Plans', value: 'ALL' },
    { label: 'Free', value: 'FREE' },
    { label: 'Basic', value: 'BASIC' },
    { label: 'Standard', value: 'STANDARD' },
    { label: 'Enterprise', value: 'ENTERPRISE' },
  ];

  subscriptionDurationOptions = [
    { label: 'Annual', value: 'ANNUAL' },
    { label: 'Monthly', value: 'MONTHLY' },
  ];

  statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
  ];

  businessTypeOptions: { label: string; value: string }[] = [];

  constructor(
    public utils: UtilsService,
    private discountService: DiscountService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public privilegeService: PrivilegeService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    this.minDate = new Date();
    this.listDiscounts();
    this.loadBusinessTypes();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }

  listDiscounts() {
    this.discounts = [];
    this.isLoading = true;
    this.discountService.listDiscounts().subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        this.discounts = json.data || [];
        this.hideProgressBar();
      },
      (error) => {
        console.log(error);
        this.hideProgressBar();
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  loadBusinessTypes() {
    this.discountService.listBusinessTypes().subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        this.businessTypeOptions = (json.data || []).map((t: any) => ({ label: t.name, value: t.name }));
      },
      (error) => {
        console.log(error);
        this.businessTypeOptions = [];
      }
    );
  }

  private buildPayload(): any {
    return {
      name: this.name,
      description: this.description,
      discountType: this.discountType,
      discountValue: this.discountValue,
      discountValidity: this.discountValidity,
      discountValidityValue: this.discountValidity === 'LIMITED' ? (this.discountValidityValue || 0) : 0,
      discountDuration: this.discountDuration,
      discountDurationValue: this.discountDuration === 'LIMITED' ? (this.discountDurationValue || 0) : 0,
      discountFor: this.discountFor,
      discountSubscriptionType: this.discountFor !== 'SMS' ? this.discountSubscriptionType : null,
      discountSubscriptionDuration: this.discountFor !== 'SMS' ? this.discountSubscriptionDuration : null,
      discountIndustries: this.discountIndustries,
      status: this.status,
      discountActivation: this.discountActivation,
      discountActivationDate: this.discountActivation === 'custom' ? this.datePipe.transform(this.discountActivationDate, 'dd-MM-yyyy') : null,
    };
  }

  private validateForm(): boolean {
    if (!this.name.trim()) {
      this.showErrorMessage('Name (coupon code) is required.');
      return false;
    }
    if (this.discountValue === null || this.discountValue === undefined) {
      this.showErrorMessage('Discount Value is required.');
      return false;
    }
    if (this.discountValidity === 'LIMITED' && (!this.discountValidityValue || this.discountValidityValue <= 0)) {
      this.showErrorMessage('Max uses is required when Validity is Limited.');
      return false;
    }
    if (this.discountDuration === 'LIMITED' && (!this.discountDurationValue || this.discountDurationValue <= 0)) {
      this.showErrorMessage('Duration (days) is required when Duration is Limited.');
      return false;
    }
    if (this.discountActivation === 'custom' && !this.discountActivationDate) {
      this.showErrorMessage('Activation Date is required when Activation is Custom.');
      return false;
    }
    return true;
  }

  addDiscount() {
    this.showProgressBar();
    if (!this.validateForm()) {
      this.hideProgressBar();
      return;
    }
    this.discountService.addDiscount(this.buildPayload()).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHideModalDialog('add_discount');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.clearFormData();
          this.listDiscounts();
        } else {
          this.showErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideProgressBar();
      },
      (error) => {
        console.log(error);
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  editDiscount() {
    this.showProgressBar();
    if (!this.validateForm()) {
      this.hideProgressBar();
      return;
    }
    this.discountService.editDiscount(this.buildPayload(), this.id).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showHideModalDialog('edit_discount');
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listDiscounts();
        } else {
          this.showErrorMessage(json.response?.displayMessage || 'An error occurred.');
        }
        this.hideProgressBar();
      },
      (error) => {
        console.log(error);
        this.showErrorMessage(AppSettings.ERROR_MSG);
        this.hideProgressBar();
      }
    );
  }

  openEditDialog(discount: any) {
    this.clearFormData();
    this.id = discount.id;
    this.name = discount.name;
    this.description = discount.description;
    this.discountType = discount.discountType;
    this.discountValue = discount.discountValue;
    this.discountValidity = discount.discountValidity;
    this.discountValidityValue = discount.discountValidityValue;
    this.discountDuration = discount.discountDuration;
    this.discountDurationValue = discount.discountDurationValue;
    this.discountFor = discount.discountFor;
    this.discountSubscriptionType = discount.discountSubscriptionType || 'ALL';
    this.discountSubscriptionDuration = discount.discountSubscriptionDuration || 'ANNUAL';
    this.discountIndustries = discount.discountIndustries || [];
    this.status = discount.status;
    this.discountActivation = discount.discountActivation || 'immediate';
    if (discount.discountActivationDate) {
      const [day, month, year] = discount.discountActivationDate.split('-').map(Number);
      this.discountActivationDate = new Date(year, month - 1, day);
    } else {
      this.discountActivationDate = undefined!;
    }
    this.showHideModalDialog('edit_discount');
  }

  confirmDeleteDialog(discount: any) {
    this.messageService.clear();
    this.confirmationService.confirm({
      message: `Are you sure you want to remove discount "${discount.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-trash',
      accept: () => {
        this.removeDiscount(discount);
      },
      reject: (type: any) => {
        if (type === ConfirmEventType.CANCEL) {
          this.messageService.add({
            key: 'br',
            severity: 'warn',
            summary: 'Cancelled',
            detail: 'You have cancelled',
          });
        }
      },
    });
  }

  removeDiscount(discount: any) {
    this.discountService.removeDiscount(discount.id).subscribe(
      (response) => {
        const json = JSON.parse(JSON.stringify(response));
        if (json.response?.status === 'SUCCESS') {
          this.showSuccessMessage(json.response.status, json.response.message);
          this.listDiscounts();
        } else {
          this.showErrorToastMsg(json.response?.message || 'An error occurred.');
        }
      },
      (error) => {
        console.log(error);
        this.showErrorToastMsg(AppSettings.ERROR_MSG);
      }
    );
  }

  getDiscountForLabel(discount: any): string {
    const opt = this.discountForOptions.find(o => o.value === discount.discountFor);
    return opt ? opt.label : discount.discountFor;
  }

  getValueLabel(discount: any): string {
    if (discount.discountType === 'PERCENT') return discount.discountValue + '%';
    return '$' + discount.discountValue;
  }

  getValidityLabel(discount: any): string {
    if (discount.discountValidity === 'UNLIMITED') return 'Unlimited';
    return discount.discountValidityValue + ' uses';
  }

  getDurationLabel(discount: any): string {
    if (discount.discountDuration === 'UNLIMITED') return 'Unlimited';
    return discount.discountDurationValue + ' days';
  }

  getActivationLabel(discount: any): string {
    if (!discount.discountActivation || discount.discountActivation === 'immediate') {
      return 'Immediate';
    }
    if (discount.discountActivationDate) {
      const [day, month, year] = discount.discountActivationDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return this.datePipe.transform(date, 'dd MMM yyyy') || discount.discountActivationDate;
    }
    return 'Custom';
  }

  onActivationChange(value: string) {
    if (value === 'immediate') {
      this.discountActivationDate = null!;
    }
  }

  isAllIndustries(discount: any): boolean {
    const industries: string[] = discount.discountIndustries || [];
    return industries.length === 0 ||
      (this.businessTypeOptions.length > 0 && industries.length === this.businessTypeOptions.length);
  }

  getIndustryLabel(discount: any): string {
    if (this.isAllIndustries(discount)) return 'All';
    const industries: string[] = discount.discountIndustries || [];
    if (industries.length <= 2) return industries.join(', ');
    return industries.slice(0, 2).join(', ');
  }

  showIndustryPopup(event: Event, discount: any, overlay: any) {
    this.activeIndustries = discount.discountIndustries || [];
    overlay.toggle(event);
  }

  clearFormData() {
    this.messageService.clear('errMsg');
    this.name = '';
    this.description = '';
    this.discountType = 'PERCENT';
    this.discountValue = null;
    this.discountValidity = 'LIMITED';
    this.discountValidityValue = 0;
    this.discountDuration = 'LIMITED';
    this.discountDurationValue = 0;
    this.discountFor = 'SUBSCRIPTION_WITHOUT_SMS';
    this.discountSubscriptionType = 'ALL';
    this.discountSubscriptionDuration = 'ANNUAL';
    this.discountIndustries = [];
    this.status = 'ACTIVE';
    this.discountActivation = 'immediate';
    this.discountActivationDate = undefined!;
    this.id = null;
  }

  showHideModalDialog(type: string) {
    switch (type) {
      case 'add_discount':
        this.clearFormData();
        this.displayAddModal = !this.displayAddModal;
        break;
      case 'edit_discount':
        this.displayEditModal = !this.displayEditModal;
        break;
    }
  }

  showDialogMaximized(event: any, dialog: Dialog) {
    if (this.innerWidth <= 640) {
      dialog.maximized = true;
    }
  }

  showProgressBar() {
    this.messageService.clear();
    this.isLoading = true;
    this.disabled = true;
  }

  hideProgressBar() {
    this.isLoading = false;
    this.disabled = false;
  }

  showErrorMessage(msg: string) {
    this.messageService.add({ key: 'errMsg', severity: 'error', detail: msg });
  }

  showErrorToastMsg(msg: string) {
    this.messageService.add({ key: 'br', severity: 'error', detail: msg });
  }

  showSuccessMessage(status: string, msg: string) {
    this.messageService.add({ key: 'br', severity: 'success', summary: status, detail: msg });
  }

  get state(): string {
    return this.utils.state;
  }
}
